import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import listaPrecioService, { ListaPrecio } from '../../services/listaPrecioService';
import ProveedorSearch from '../../components/ProveedorSearch';
import TipoMonedaSearch from '../../components/TipoMonedaSearch';
import ArticuloXListaPrecioPanel from './ArticuloXListaPrecio';

const defaultForm: ListaPrecio = {
  codigoLista: '',
  descripcion: '',
  cp: '',
  nroProveedor: 0,
  fechaCreacion: new Date().toISOString().split('T')[0],
  codigoMoneda: '1',
  porcGan: 0,
  actAutom: 'N',
  variacionFp: 'N',
  variacionBasadaEn: '',
  variacionSigno: '',
  sincronizar: 0
};

const ListaPrecioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoLista } = useParams();
  const [formData, setFormData] = useState<ListaPrecio>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [monedaSearchOpen, setMonedaSearchOpen] = useState(false);

  const loadListaPrecio = useCallback(async () => {
    if (!codigoLista || codigoLista === 'nuevo') return;
    try {
      setLoading(true);
      const lista = await listaPrecioService.getListaPrecio(codigoLista);
      setFormData(lista);
    } catch (error) {
      enqueueSnackbar('Error al cargar la lista de precios', { variant: 'error' });
      navigate('/ventas/listaprecios');
    } finally {
      setLoading(false);
    }
  }, [codigoLista, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoLista && codigoLista !== 'nuevo') loadListaPrecio();
  }, [codigoLista, loadListaPrecio]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroProveedor' || name === 'porcGan' || name === 'sincronizar') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProveedorSelect = (proveedor: any) => {
    setFormData(prev => ({
      ...prev,
      nroProveedor: proveedor.nroProveedor
    }));
    setProveedorSearchOpen(false);
  };

  const handleMonedaSelect = (moneda: any) => {
    setFormData(prev => ({
      ...prev,
      codigoMoneda: moneda.codigoMoneda.toString()
    }));
    setMonedaSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.codigoLista.trim() || !formData.descripcion.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (codigoLista && codigoLista !== 'nuevo') {
        await listaPrecioService.updateListaPrecio(codigoLista, formData);
      } else {
        await listaPrecioService.createListaPrecio(formData);
      }

      enqueueSnackbar('Lista de precios guardada exitosamente', { variant: 'success' });
      navigate('/ventas/listaprecios');
    } catch (error: any) {
      console.error('Error al guardar lista de precios:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la lista de precios', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/listaprecios')}
              sx={{ textTransform: 'none' }}
            >
              Listas de Precios
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {codigoLista && codigoLista !== 'nuevo' ? 'Editar Lista de Precios' : 'Nueva Lista de Precios'}
            </Typography>
          </Stack>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
            <Tab label="Variaciones" />
            {codigoLista && codigoLista !== 'nuevo' && <Tab label="Artículos" />}
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="codigoLista" 
                    label="Código" 
                    value={formData.codigoLista} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(codigoLista && codigoLista !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="descripcion" 
                    label="Descripción" 
                    value={formData.descripcion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Proveedor" 
                      value={formData.nroProveedor || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setProveedorSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Moneda" 
                      value={formData.codigoMoneda || ''} 
                      fullWidth
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setMonedaSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="porcGan" 
                    label="% Ganancia" 
                    type="number"
                    value={formData.porcGan} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="fechaCreacion" 
                    label="Fecha Creación" 
                    type="date"
                    value={typeof formData.fechaCreacion === 'string' ? formData.fechaCreacion.split('T')[0] : ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="fechaCaducacion" 
                    label="Fecha Caducación" 
                    type="date"
                    value={formData.fechaCaducacion ? (typeof formData.fechaCaducacion === 'string' ? formData.fechaCaducacion.split('T')[0] : '') : ''} 
                    onChange={handleChange} 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="observacion" 
                    label="Observación" 
                    value={formData.observacion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    name="variacionFp"
                    label="Variación Forma de Pago"
                    value={formData.variacionFp || 'N'}
                    onChange={(v) => setFormData(prev => ({ ...prev, variacionFp: v ?? 'N' }))}
                    options={[
                      { value: 'S', label: 'Sí' },
                      { value: 'N', label: 'No' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="variacionBasadaEn" 
                    label="Variación Basada En" 
                    value={formData.variacionBasadaEn || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab !== 3 && (
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={saving}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/ventas/listaprecios')} 
                  disabled={saving}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  Cancelar
                </Button>
              </Stack>
            )}
          </form>

          {currentTab === 3 && codigoLista && codigoLista !== 'nuevo' && (
            <Box sx={{ mt: 2 }}>
              <ArticuloXListaPrecioPanel codigoListaFijo={codigoLista} />
            </Box>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={proveedorSearchOpen}
        onClose={() => setProveedorSearchOpen(false)}
        onSelect={handleProveedorSelect}
      />

      <TipoMonedaSearch
        open={monedaSearchOpen}
        onClose={() => setMonedaSearchOpen(false)}
        onSelect={handleMonedaSelect}
      />
    </Box>
  );
};

export default ListaPrecioForm;
